import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LogOut } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { usersApi } from '@/lib/api';
import { useMe } from '@/lib/me';

const BANNER_HEIGHT_PX = 40;

/**
 * Sticky amber bar shown when the current session is impersonating. Uses
 * `position: fixed` at the viewport top so toggling visibility doesn't
 * reflow the app shell — the matching CSS in <AppLayout> reserves space
 * via padding-top when this is active.
 *
 * Stop mirrors Start's strategy: clear the query cache and hard-nav to '/'.
 * The identity boundary is the same in both directions; cached lists
 * rendered for the impersonated user must NOT persist into the operator's
 * session (and vice versa).
 */
export function ImpersonationBanner({ offsetTop = 0 }: { offsetTop?: number }) {
  const { impersonation, user } = useMe();
  const queryClient = useQueryClient();

  const stop = useMutation({
    mutationFn: () => usersApi.stopImpersonation(),
    onSuccess: () => {
      queryClient.clear();
      window.location.assign('/');
    },
  });

  if (!impersonation.impersonating) return null;
  const real = impersonation.realUser;
  const acting = user ? `${user.firstName} ${user.lastName}` : 'another user';

  return (
    <div
      role="status"
      style={{ height: BANNER_HEIGHT_PX, top: offsetTop }}
      className="fixed inset-x-0 top-0 z-50 flex items-center justify-between gap-3 border-b border-amber-300 bg-amber-100 px-4 text-sm text-amber-900"
    >
      <div>
        <span className="font-semibold">Impersonating {acting}</span>
        <span className="ml-2 opacity-80">
          (you are signed in as {real.firstName} {real.lastName} · {real.email})
        </span>
      </div>
      <Button
        size="sm"
        variant="outline"
        className="border-amber-300 bg-white text-amber-900 hover:bg-amber-50"
        onClick={() => stop.mutate()}
        disabled={stop.isPending}
      >
        <LogOut />
        Stop impersonation
      </Button>
    </div>
  );
}

/** Height the layout must reserve above the app shell when impersonating. */
export const IMPERSONATION_BANNER_HEIGHT = BANNER_HEIGHT_PX;
